import { useInfiniteQuery } from 'react-query';

import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
  infiniteEmptyResults,
} from 'data_sources/constants';

import contentHelper from 'helpers/contentHelper';

import type { ManualItem } from 'views/App/Portal/Policy/_types/manuals/issued';
import type { ApiCollectionResponse } from 'types/api_response';

export default function useIssuedManuals<T extends ManualItem>(
  organizationId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexipub/manuals/issued/v1/manuals`;

  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['issued_manuals', organizationId, params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return convertResults(data) as ApiCollectionResponse<T>;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}

const convertResults = (data: any) => ({
  count: data.count,
  offset: data.offset || 0,
  content: data.content.map((x: any) => ({
    authorizations: x.authorizations,
    id: x.id,
    manualType: {
      key: x.manualType?.key,
      name: x.manualType?.value,
    },
    name: contentHelper.decodeHtmlEntities(x.name?.value || ''),
    organizationId: x.organizationId,
    organization: x.organization,
    s3Location: x.s3Location,
    createdAt: x.createdAt,
    publishedAt: x.publishedAt,
    jurisdiction: x.jurisdiction,
    vertical: {
      key: x.vertical?.key,
      name: x.vertical?.value,
    },
  })),
});
